import { IFutureOrder, ISupplierExposure } from '@sp-types/FutureOrderType';

import React from 'react';
import { MaterialDataCard } from '../../../components/UI-Elements/Grids/MaterialDataGrid';
import { Material } from '../../../sp-types/materialType';
import { formatToPriceInDollars, formatToShortString } from '../../../utils/FormatValue';
import { getUnitObj } from '../../../utils/MassUnits/massUnitsUtils';
import TransactionsGrid from '../../Inventory/inventory-components/TransactionGrid/TransactionsGrid';
import { getCommitmentsCols } from '../../Inventory/inventory-components/TransactionGrid/GridColumns';
import {
  flattenSalesCommits,
  groupSalesByMaterial,
  SalesCommItem,
  GroupedSalesCommItem,
  groupSalesCommitsByMonth,
  groupSalesCommitsByCustomer,
} from './SalesCommUtils';
import { GENERAL_ID } from '../../../components/Stores/exposureDataStore';
import { ToggleChips } from '../../Dashboard/dashboard-components/ToggleChips';
import * as XLSX from 'xlsx';
import { useAccountStore } from '../../../components/Stores/accountStore';
import { Button } from '@mui/material';

export interface MaterialCard {
  key: string;
  name: string;
  color: string;
  quantity: number;
  tonPrice: number;
  totalPrice: number;
  fromAverage: number;
}

interface FutureOrdersByMaterial {
  materialData: ISupplierExposure;
  orderId: string;
  orderProductQuantity: number;
  orderUuid?: string;
  futureOrderType?: 'BackToBack' | 'Indexed';
  order: IFutureOrder;
}

const mapFutureOrdersByMaterial = (
  futureOrders: IFutureOrder[],
  materialIds?: string[],
): { data: FutureOrdersByMaterial[]; materialId: string }[] => {
  const materialMap = futureOrders.reduce((acc, order) => {
    // wrong code... too complex
    order.supplierExposure.forEach((materialData) => {
      if (!materialIds || materialIds.includes(materialData.materialId)) {
        const futureOrder: FutureOrdersByMaterial = {
          materialData,
          orderId: order.orderId,
          orderUuid: order.uuid,
          futureOrderType: order.futureOrderType,
          orderProductQuantity: order.orderDetails.quantity,
          order,
        };
        if (!acc[materialData.materialId]) acc[materialData.materialId] = [];
        acc[materialData.materialId].push(futureOrder);
      }
    });
    return acc;
  }, {} as Record<string, FutureOrdersByMaterial[]>);

  return Object.entries(materialMap).map(([materialId, data]) => ({ materialId, data }));
};

interface Props {
  allFutureOrders: IFutureOrder[];
  materialId: string;
  setLoading: (_loading: boolean) => void;
  refetchOrders: () => void;
  isEditEnabled: boolean;
  isAdmin: boolean;
  colors: Record<string, string>;
  materialsMap: Record<string, Material>;
  pricesMap: Record<string, number>;
  onEditCommitment?: (_order: IFutureOrder) => void;
}

export const CommitmentsList: React.FC<Props> = ({
  allFutureOrders,
  materialId,
  setLoading,
  refetchOrders,
  isEditEnabled,
  colors,
  materialsMap,
  pricesMap,
  isAdmin,
  onEditCommitment,
}) => {
  const { account } = useAccountStore();
  const [materialExpanded, setMaterialExpanded] = React.useState<string>('');
  const [flatSalesCommits, setFlatSalesCommits] = React.useState<SalesCommItem[]>(flattenSalesCommits(allFutureOrders));
  const [groupByIndex, setGroupByIndex] = React.useState<number>(1); // Default to Month
  const [expandedGroups, setExpandedGroups] = React.useState<Set<string>>(new Set());

  const handleExportToExcel = React.useCallback(() => {
    const today = new Date().toISOString().split('T')[0];
    const filename = `Salescomm-${account.id || 'unknown'}-${today}.xlsx`;

    // Flatten all sales commitments from all future orders
    const allCommits = flattenSalesCommits(allFutureOrders);

    const exportData = allCommits.map((commit) => ({
      'Material Name': materialsMap[commit.materialId]?.name || commit.materialId,
      'Material ID': commit.materialId,
      'Client Name': commit.clientName,
      'Order ID': commit.orderId,
      'Due Date': commit.date?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) || '',
      Quantity: commit.value,
      Unit: getUnitObj(materialsMap[commit.materialId]?.unit).shortForm,
      Currency: materialsMap[commit.materialId]?.currency || 'USD',
      'Actual Price': commit.actualPrice || '-',
      'Total Actual Price': commit.actualPrice ? (commit.actualPrice * commit.value).toFixed(2) : '-',
      'Market Price': pricesMap[commit.materialId] || '-',
      'Total Market Value': pricesMap[commit.materialId]
        ? (pricesMap[commit.materialId] * commit.value).toFixed(2)
        : '-',
      Excluded: commit.dontSum ? 'Excluded' : '',
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sales Commitments');
    XLSX.writeFile(wb, filename);
  }, [allFutureOrders, materialsMap, pricesMap, account.id]);

  const handleEditRow = (uuid: string) => {
    const order = allFutureOrders.find((o) => o.uuid === uuid);
    if (order && onEditCommitment) {
      onEditCommitment(order);
    }
  };

  const groupByOptions = [
    { key: 'None', value: 0 },
    { key: 'Month', value: 1 },
    { key: 'Customer', value: 2 },
  ];

  const groupBy = groupByOptions[groupByIndex].key as 'None' | 'Month' | 'Customer';

  React.useEffect(() => {
    if (allFutureOrders) {
      setFlatSalesCommits(flattenSalesCommits(allFutureOrders));
    }
  }, [allFutureOrders]);

  const getProcessedDataForMaterial = React.useCallback(
    (forMaterialId: string) => {
      // Filter by material first
      const filtered = flatSalesCommits.filter((item) => item.materialId === forMaterialId);
      const sorted = filtered.sort((a, b) => a.date.getTime() - b.date.getTime());

      if (groupBy === 'Month') {
        const grouped = groupSalesCommitsByMonth(sorted);
        return grouped.filter((item) => {
          if ((item as GroupedSalesCommItem).isGroup) return true;
          const monthKey = item.date.toISOString().substring(0, 7);
          return expandedGroups.has(monthKey);
        });
      } else if (groupBy === 'Customer') {
        const grouped = groupSalesCommitsByCustomer(sorted);
        return grouped.filter((item) => {
          if ((item as GroupedSalesCommItem).isGroup) return true;
          return expandedGroups.has(item.clientName);
        });
      }

      return sorted;
    },
    [flatSalesCommits, groupBy, expandedGroups],
  );

  const handleGroupToggle = (groupKey: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }
      return newSet;
    });
  };

  return flatSalesCommits ? (
    <div className="flex flex-col bg-white p-3 gap-2 overflow-y-auto">
      <div className="flex flex-row items-center gap-2 justify-between">
        <div className="flex flex-row items-center gap-2">
          <span>Group by</span>
          <ToggleChips chips={groupByOptions} selected={groupByIndex} setSelected={setGroupByIndex} width="6rem" />
        </div>
        <Button
          onClick={handleExportToExcel}
          variant="outlined"
          sx={{
            textTransform: 'none',
            color: '#16a34a',
            borderColor: '#16a34a',
            padding: '2px 4px',
            '&:hover': {
              borderColor: '#15803d',
              backgroundColor: 'rgba(22, 163, 74, 0.04)',
            },
          }}
        >
          <span className="mr-1.5">📊</span>
          Export to Excel
        </Button>
      </div>
      <MaterialDataCard
        materialColorDictionary={colors}
        onExpandToggle={(materialId) => setMaterialExpanded((prev) => (prev === materialId ? '' : materialId))}
        materialsData={Object.entries(groupSalesByMaterial(flatSalesCommits))
          .filter(([matId]) => materialId === GENERAL_ID || matId === materialId)
          .map(([materialId, totals]) => ({ ...totals, materialId, unit: materialsMap[materialId]?.unit }))
          .map(({ materialId, quantity, unit, toDate, fromDate }) => ({
            materialId,
            materialNameSize: 230,
            expanded: materialExpanded === materialId,
            data: [
              {
                title: 'Date range',
                valueAsString: `${fromDate.toLocaleDateString()} - ${toDate.toLocaleDateString()}`,
                size: 190,
              },
              {
                title: 'Total quantity',
                valueAsString: `${formatToShortString(quantity, 1)} ${getUnitObj(unit).shortForm}`,
                size: 220,
              },
              {
                title: `Value (${getUnitObj(unit).shortForm})`,
                valueAsString: formatToPriceInDollars(pricesMap[materialId], 1) || 'N/A',
                size: 150,
              },

              {
                title: 'Total value',
                valueAsString: formatToPriceInDollars(quantity * (pricesMap[materialId] || 1)),
                size: 220,
              },
            ],
          }))}
        materialsMap={materialsMap}
      >
        <div className="flex flex-row h-full ps-20">
          <TransactionsGrid
            inventoryReports={getProcessedDataForMaterial(materialExpanded) as any[]}
            colorDictionary={colors}
            {...{ materialsMap, isEditEnabled, allFutureOrders, isAdmin, materialId: materialExpanded }}
            refetchInventory={refetchOrders}
            onEditRow={(id, row) => row.uuid && handleEditRow(row.uuid)}
            columns={getCommitmentsCols(
              pricesMap[materialExpanded] || 0,
              getUnitObj(materialsMap[materialExpanded]?.unit).shortForm,
              groupBy !== 'None',
              handleGroupToggle,
              expandedGroups,
              materialsMap,
            )}
            exportType="salescomm"
          />
        </div>
      </MaterialDataCard>
    </div>
  ) : (
    <div> Loading...</div>
  );
};
